/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Skeleton } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import {
  buildRankingStackedSeries,
  formatBRL,
  mapRankingToChartDataset,
} from './constants';
import {
  ChartEmpty,
  CHART_HEIGHT_RANKING,
  getRankingChartLayout,
  RANKING_INLINE_MAX_HEIGHT,
  RANKING_ROW_HEIGHT,
} from './chartShared';

const chartWrapperSx = {
  width: '100%',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  '& .MuiChartsLegend-root': { display: 'none' },
  '& .MuiChartsAxis-root': { '& text': { textAnchor: 'start' } },
};

const VeiculoRankingChart = ({ ranking, loading, height, onSelectPlaca }) => {
  const isExpanded = height != null;
  const dataset = useMemo(() => mapRankingToChartDataset(ranking), [ranking]);
  const series = useMemo(() => buildRankingStackedSeries(), []);
  const chartLayout = useMemo(() => getRankingChartLayout(dataset), [dataset]);
  const scrollableHeight = Math.max(
    CHART_HEIGHT_RANKING,
    dataset.length * RANKING_ROW_HEIGHT + 48,
  );
  const chartHeight = isExpanded ? (height || scrollableHeight) : scrollableHeight;
  const hasData = dataset.length > 0;

  const handleItemClick = useCallback(
    (_event, identifier) => {
      if (!onSelectPlaca || identifier?.dataIndex == null) return;
      const row = dataset[identifier.dataIndex];
      if (row?.placa) onSelectPlaca(row.placa);
    },
    [dataset, onSelectPlaca],
  );

  const chart = (
    <BarChart
      layout="horizontal"
      dataset={dataset}
      series={series}
      yAxis={[
        {
          scaleType: 'band',
          dataKey: 'label',
          width: chartLayout.yAxisWidth,
          tickLabelStyle: {
            fontSize: 12,
            textAnchor: 'start',
          },
        },
      ]}
      xAxis={[{ min: 0, valueFormatter: (value) => formatBRL(value) }]}
      height={chartHeight}
      margin={chartLayout.margin}
      slotProps={{ legend: { hidden: true } }}
      onItemClick={onSelectPlaca ? handleItemClick : undefined}
      sx={{
        width: '100%',
        maxWidth: '100%',
        ml: 0,
        cursor: onSelectPlaca ? 'pointer' : 'default',
      }}
    />
  );

  if (loading) {
    return (
      <Skeleton
        variant="rounded"
        height={isExpanded ? chartHeight : RANKING_INLINE_MAX_HEIGHT}
      />
    );
  }

  if (!hasData) {
    return <ChartEmpty message="Sem dados no período selecionado" />;
  }

  return (
    <Box sx={{ width: '100%' }}>
      {isExpanded ? (
        <Box sx={chartWrapperSx}>{chart}</Box>
      ) : (
        <Box
          sx={{
            ...chartWrapperSx,
            maxHeight: RANKING_INLINE_MAX_HEIGHT,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {chart}
        </Box>
      )}
    </Box>
  );
};

VeiculoRankingChart.propTypes = {
  ranking: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  height: PropTypes.number,
  onSelectPlaca: PropTypes.func,
};

VeiculoRankingChart.defaultProps = {
  ranking: [],
  loading: false,
  height: null,
  onSelectPlaca: null,
};

export default VeiculoRankingChart;
